// Lightweight hash router: #/today, #/space/<id>?view=board, #/task/HUM-12 …
import { useSyncExternalStore } from "react";

export type Route =
  | { name: "today" }
  | { name: "inbox" }
  | { name: "upcoming" }
  | { name: "review" }
  | { name: "snoozed" }
  | { name: "all" }
  | { name: "area"; id: string }
  | { name: "space"; id: string }
  | { name: "project"; id: string }
  | { name: "task"; readableId: string }
  | { name: "settings" }
  | { name: "search" };

export type RouteName = Route["name"];

export interface Location {
  route: Route;
  path: string;
  params: URLSearchParams;
}

const SIMPLE = new Set(["today", "inbox", "upcoming", "review", "snoozed", "all", "settings", "search"]);

export function parseHash(hash: string): Location {
  const raw = hash.replace(/^#\/?/, "");
  const [pathPart = "", queryPart = ""] = raw.split("?");
  const params = new URLSearchParams(queryPart);
  const segs = pathPart.split("/").filter(Boolean).map(decodeURIComponent);
  const [head, arg] = segs;
  let route: Route = { name: "today" };
  if (head && SIMPLE.has(head)) route = { name: head } as Route;
  else if ((head === "area" || head === "space" || head === "project") && arg) route = { name: head, id: arg };
  else if (head === "task" && arg) route = { name: "task", readableId: arg };
  return { route, path: routePath(route), params };
}

export function routePath(route: Route): string {
  switch (route.name) {
    case "area":
    case "space":
    case "project":
      return `${route.name}/${encodeURIComponent(route.id)}`;
    case "task":
      return `task/${encodeURIComponent(route.readableId)}`;
    default:
      return route.name;
  }
}

export function href(route: Route, params?: Record<string, string | undefined>): string {
  const p = new URLSearchParams();
  if (params) for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== "") p.set(k, v);
  const q = p.toString();
  return `#/${routePath(route)}${q ? `?${q}` : ""}`;
}

let current = typeof window === "undefined" ? parseHash("") : parseHash(window.location.hash);
const listeners = new Set<() => void>();
if (typeof window !== "undefined") {
  window.addEventListener("hashchange", () => {
    current = parseHash(window.location.hash);
    listeners.forEach((l) => l());
  });
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function useLocation(): Location {
  return useSyncExternalStore(
    subscribe,
    () => current,
    () => current,
  );
}

export function getLocation(): Location {
  return current;
}

export function navigate(route: Route, params?: Record<string, string | undefined>, opts: { replace?: boolean } = {}) {
  const next = href(route, params);
  if (next === window.location.hash) return;
  if (opts.replace) {
    history.replaceState(history.state, "", next);
    current = parseHash(next);
    listeners.forEach((l) => l());
  } else {
    window.location.hash = next;
  }
}

/** Replace the whole hash without a history entry (launch route). */
export function replaceHash(hash: string) {
  history.replaceState(history.state, "", hash);
  current = parseHash(hash);
  listeners.forEach((l) => l());
}

/** Update query params on the current route (replace, so filters don't flood history). */
export function setParams(patch: Record<string, string | undefined>) {
  const p = new URLSearchParams(current.params);
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined || v === "") p.delete(k);
    else p.set(k, v);
  }
  const obj: Record<string, string> = {};
  p.forEach((v, k) => (obj[k] = v));
  navigate(current.route, obj, { replace: true });
}

export function sameRoute(a: Route, b: Route): boolean {
  return routePath(a) === routePath(b);
}
